import React, { useState } from 'react';
import { ClipboardList, FileCheck2, UserCog, Paperclip, PenLine, Send } from 'lucide-react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ReferralFormModal from '../../components/ReferralFormModal';
import { downloadBlankIntakePacket } from '../../utils/intakePacketPdf';

const FUNCTIONAL_CRITERIA = [
  'Marked inability to establish or maintain competitive employment',
  'Marked inability to perform instrumental activities of daily living',
  'Marked inability to establish/maintain a personal support system',
  'Deficiencies in concentration, persistence, or pace',
  'Marked deficiencies in self-direction',
  'Marked inability to procure financial assistance'
];

const REQUIRED_DOCS = [
  'Psychiatric evaluation', 'Diagnostic assessment', 'Progress notes', 'Medication list',
  'Insurance card (copy)', 'Referral script / order', 'Consent forms', 'Discharge summary',
  "School records / IEP (for minors)", 'Labs / medical records', 'Release of Information (ROI)',
  'Treatment history summary', 'Court orders (if applicable)'
];

const ReferralRequirementsPage = () => {
  const [showReferralForm, setShowReferralForm] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Referral Requirements"
        description="What providers and agencies need to submit an OMHC or PRP referral to SDORA Wellbeing: required provider info, client details, functional criteria, and attachments."
        path="/providers/referral-requirements"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="referral-requirements-title">
            Referral Requirements
          </h1>
          <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl">
            Everything providers and agencies need to prepare a complete OMHC or PRP referral packet for SDORA Wellbeing.
          </p>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

          {/* Referral Categories */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <ClipboardList className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Referral Categories</h2>
            </div>
            <p className="text-gray-700 mb-4">Our referral form supports three categories, selected at the start of the referral:</p>
            <ul className="grid sm:grid-cols-3 gap-3">
              <li className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm font-medium text-indigo-900">OMHC — Outpatient Mental Health Clinic</li>
              <li className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm font-medium text-indigo-900">PRP — Psychiatric Rehabilitation Program</li>
              <li className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm font-medium text-indigo-900">Both OMHC and PRP</li>
            </ul>
          </section>

          {/* Referring Provider Info */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCog className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Referring Provider Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Referring provider name, credentials, and NPI number</li>
              <li>Contact phone, fax, and email address</li>
              <li>If the referring clinician is under clinical supervision: supervisor name, credentials, NPI, phone, and email</li>
              <li>Preferred contact method and urgency level (Routine, Priority, Urgent)</li>
            </ul>
          </section>

          {/* Client Info */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileCheck2 className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Client Contact Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Full home address (line 1, city, state, ZIP)</li>
              <li>Client phone number</li>
              <li>Current diagnosis and a summary of presenting symptoms / reason for referral</li>
            </ul>
          </section>

          {/* PRP Functional Criteria */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <ClipboardList className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">PRP Functional Criteria (Adults)</h2>
            </div>
            <p className="text-gray-700 mb-4">
              For adult PRP referrals, please identify <strong>at least 3</strong> of the following functional impairments,
              each with supporting evidence:
            </p>
            <ul className="space-y-2">
              {FUNCTIONAL_CRITERIA.map((criterion) => (
                <li key={criterion} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 flex-shrink-0" />
                  {criterion}
                </li>
              ))}
            </ul>
          </section>

          {/* Attachments */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Paperclip className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Supporting Documentation</h2>
            </div>
            <p className="text-gray-700 mb-4">Please attach any applicable documents. At minimum, most referrals require a diagnostic assessment, consent forms, and insurance information.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {REQUIRED_DOCS.map((doc) => (
                <div key={doc} className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-700">{doc}</div>
              ))}
            </div>
          </section>

          {/* Signature */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <PenLine className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Signature & Authorization</h2>
            </div>
            <p className="text-gray-700">
              Every referral requires the referring provider's signature (typed or drawn), the date, and agreement to
              the authorization statement confirming the information provided is accurate.
            </p>
          </section>

          {/* Submit CTA */}
          <section className="bg-indigo-50 border border-indigo-100 rounded-xl p-8 text-center">
            <div className="flex items-center gap-3 justify-center mb-3">
              <Send className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Ready to Submit?</h2>
            </div>
            <p className="text-gray-700 mb-6">Complete our secure online referral form, or download a blank packet to fill out and fax back.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setShowReferralForm(true)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                data-testid="referral-requirements-submit-btn"
              >
                Submit a Referral
              </button>
              <button
                onClick={downloadBlankIntakePacket}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                data-testid="referral-requirements-download-packet-btn"
              >
                Preview Client Intake Packet
              </button>
            </div>
          </section>
        </div>
      </div>

      <Footer />

      {showReferralForm && <ReferralFormModal onClose={() => setShowReferralForm(false)} />}
    </div>
  );
};

export default ReferralRequirementsPage;
